import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import React, { useContext, useEffect, useCallback } from 'react';
export var KeyboardHighlightContext = /*#__PURE__*/React.createContext({
  registerKeyboardEnabled: undefined,
  unregisterKeyboardEnabled: undefined,
  getSelectedIndex: undefined,
  highlightComponentAt: undefined,
  unhighlightComponentAt: undefined,
  unhighlightAll: undefined,
  keyboardNavComponents: []
});
export var useKeyboardNavigation = function useKeyboardNavigation(options) {
  var _options$navigationTr;
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    ref = _React$useState2[0],
    setRef = _React$useState2[1];
  var registrationId = React.useRef(null);
  var navigationTrackingId = (_options$navigationTr = options === null || options === void 0 ? void 0 : options.navigationTrackingId) !== null && _options$navigationTr !== void 0 ? _options$navigationTr : null;
  var _useContext = useContext(KeyboardHighlightContext),
    registerKeyboardEnabled = _useContext.registerKeyboardEnabled,
    unregisterKeyboardEnabled = _useContext.unregisterKeyboardEnabled;
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    isHighlighted = _React$useState4[0],
    setIsHighlighted = _React$useState4[1];
  var onKeydownCallback = options === null || options === void 0 ? void 0 : options.onKeydownCallback;
  var onHighlightedCallback = options === null || options === void 0 ? void 0 : options.onHighlightedCallback;
  var unregister = useCallback(function () {
    if (registrationId.current && unregisterKeyboardEnabled) {
      // If the ref got removed or changed we unregister the current ref
      unregisterKeyboardEnabled(registrationId.current);
    }
  }, [unregisterKeyboardEnabled]);

  // Will unmount effect
  useEffect(function () {
    return function () {
      return unregister();
    };
  }, [unregister]);

  // Ref change to null
  useEffect(function () {
    !ref && unregister();
  }, [ref, unregister]);

  // Everything else
  useEffect(function () {
    if (!registerKeyboardEnabled || !unregisterKeyboardEnabled) {
      return;
    }
    if (!ref) {
      return;
    }

    // Then we register the new ref
    if (ref) {
      // Another check here to ensure that the ref we get is actually a DOM node, there's probably better
      // ways to make this check but it doesn't matter too much.
      // If what we get is NOT a DOM node we will log a warning and default back to use React.findDOMNode
      if (!ref.compareDocumentPosition || !ref.addEventListener || !ref.removeEventListener) {
        // eslint-disable-next-line
        console.error('It is expected that any keyboard highlighting enabled component also passes the ref all the way to the DOM node.', 'It appears that the `ref` is not fowarded all the way to the DOM node, please use `React.forwardRef to ensure this is so`', 'Falling back to using ReactDOM.findDOMNode, this is not recommended and this fallback may be removed in the future');
        return;
      }
      registrationId.current = registerKeyboardEnabled(registrationId.current, ref, setIsHighlighted, onKeydownCallback, navigationTrackingId, onHighlightedCallback);
    }
  }, [ref, setIsHighlighted, onKeydownCallback, registerKeyboardEnabled, unregisterKeyboardEnabled, navigationTrackingId, onHighlightedCallback]);
  return [isHighlighted, setRef];
};
export var KeyboardHighlightProvider = /*#__PURE__*/function (_React$Component) {
  function KeyboardHighlightProvider() {
    var _this;
    _classCallCheck(this, KeyboardHighlightProvider);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _callSuper(this, KeyboardHighlightProvider, [].concat(args));
    _defineProperty(_this, "currentlySelected", undefined);
    _defineProperty(_this, "currentSelectedTrackingId", null);
    _defineProperty(_this, "keyboardNavComponents", []);
    _defineProperty(_this, "registerKeyboardEnabled", function (currentId, domRef, onHighlightChanged, onKeyDownWhenHighlighted, navigationTrackingId, onHighlightedCallback) {
      var indexAt = _this.keyboardNavComponents.findIndex(function (_ref) {
        var id = _ref.id;
        return id === currentId;
      });
      // If the currentId is null or we can't find any records of the id we will create a new entry
      var isNew = currentId === null || currentId === undefined || indexAt === -1;
      var id = isNew ? Math.max.apply(Math, _toConsumableArray(_this.keyboardNavComponents.map(function (component) {
        return component.id;
      })).concat([0])) + 1 : _this.keyboardNavComponents[indexAt].id;
      if (isNew) {
        _this.keyboardNavComponents.push({
          id: id,
          domRef: domRef,
          onHighlightChanged: onHighlightChanged,
          onKeyDownWhenHighlighted: onKeyDownWhenHighlighted,
          navigationTrackingId: navigationTrackingId,
          onHighlightedCallback: onHighlightedCallback
        });
      } else {
        // Otherwise we will update the current entry
        _this.keyboardNavComponents[indexAt] = {
          id: id,
          domRef: domRef,
          onHighlightChanged: onHighlightChanged,
          onKeyDownWhenHighlighted: onKeyDownWhenHighlighted,
          navigationTrackingId: navigationTrackingId,
          onHighlightedCallback: onHighlightedCallback
        };
      }
      _this.keyboardNavComponents = _this.keyboardNavComponents.sort(function (c1, c2) {
        var result = c1.domRef.compareDocumentPosition(c2.domRef);
        if (result & Node.DOCUMENT_POSITION_FOLLOWING || result & Node.DOCUMENT_POSITION_CONTAINED_BY) {
          return -1;
        } else if (result & Node.DOCUMENT_POSITION_PRECEDING || result & Node.DOCUMENT_POSITION_CONTAINS) {
          return 1;
        }
        return 0;
      });
      var indexToHighlight = _this.getSearchIdIndex(_this.currentSelectedTrackingId);
      if (indexToHighlight !== undefined && indexToHighlight >= 0) {
        _this.unhighlightAll();
        _this.highlightComponentAt(indexToHighlight);
      }
      _this.keyboardNavComponents = _toConsumableArray(_this.keyboardNavComponents);
      return id;
    });
    _defineProperty(_this, "unregisterKeyboardEnabled", function (unregisterRef) {
      if (_this.currentlySelected === unregisterRef) {
        _this.currentlySelected = undefined;
      }
      _this.keyboardNavComponents = _this.keyboardNavComponents.filter(function (_ref2) {
        var ref = _ref2.id;
        return ref !== unregisterRef;
      });
    });
    _defineProperty(_this, "onKeyDownCallbackAt", function (index, e) {
      if (typeof index !== 'undefined' && index >= 0 && index < _this.keyboardNavComponents.length) {
        var onKeyDown = _this.keyboardNavComponents[index].onKeyDownWhenHighlighted;
        onKeyDown && onKeyDown(e, _this.keyboardNavComponents[index].domRef);
      }
    });
    _defineProperty(_this, "onHighlightedCallback", function (index) {
      if (typeof index !== 'undefined' && index >= 0 && index < _this.keyboardNavComponents.length) {
        var callback = _this.keyboardNavComponents[index].onHighlightedCallback;
        callback && callback(index);
      }
    });
    _defineProperty(_this, "unhighlightComponentAt", function (index) {
      if (typeof index !== 'undefined' && index >= 0 && index < _this.keyboardNavComponents.length) {
        _this.keyboardNavComponents[index].onHighlightChanged(false);
      }
    });
    _defineProperty(_this, "highlightComponentAt", function (index) {
      if (index >= 0 && index < _this.keyboardNavComponents.length) {
        var _this$keyboardNavComp = _this.keyboardNavComponents[index],
          id = _this$keyboardNavComp.id,
          _navigationTrackingId = _this$keyboardNavComp.navigationTrackingId;
        _this.currentlySelected = id;
        _this.currentSelectedTrackingId = _navigationTrackingId;
        _this.keyboardNavComponents[index].onHighlightChanged(true);
      }
    });
    _defineProperty(_this, "unhighlightAll", function () {
      _this.currentSelectedTrackingId = null;
      _this.currentlySelected = undefined;
      _this.keyboardNavComponents.forEach(function (component) {
        return component.onHighlightChanged(false);
      });
    });
    _defineProperty(_this, "getSelectedIndex", function () {
      if (!_this.currentlySelected) {
        return undefined;
      }
      return _this.keyboardNavComponents.findIndex(function (_ref3) {
        var id = _ref3.id;
        return id === _this.currentlySelected;
      });
    });
    _defineProperty(_this, "getSearchIdIndex", function (searchId) {
      return searchId === null ? undefined : _this.keyboardNavComponents.findIndex(function (_ref4) {
        var navigationTrackingId = _ref4.navigationTrackingId;
        return navigationTrackingId === searchId;
      });
    });
    _defineProperty(_this, "onKeyDown", function (e) {
      if (_this.keyboardNavComponents.length === 0) {
        return;
      }
      if (e.isComposing || e.keyCode === 229) {
        return;
      }
      var currentIndex = _this.getSelectedIndex();
      var prevIndex = currentIndex;
      if (e.code === 'ArrowDown' || e.keyCode === 40) {
        if (!e.shiftKey) {
          if (typeof currentIndex === 'undefined') {
            currentIndex = 0;
          } else {
            currentIndex = (currentIndex + 1) % _this.keyboardNavComponents.length;
          }
          e.preventDefault();
        }
      } else if (e.code === 'ArrowUp' || e.keyCode === 38) {
        if (!e.shiftKey) {
          if (typeof currentIndex === 'undefined') {
            currentIndex = _this.keyboardNavComponents.length - 1;
          } else {
            currentIndex -= 1;
            if (currentIndex < 0) {
              currentIndex = _this.keyboardNavComponents.length - Math.abs(currentIndex);
            }
          }
          e.preventDefault();
        }
      } else {
        if (typeof currentIndex !== 'undefined') {
          _this.onKeyDownCallbackAt(currentIndex, e);
        }
        return;
      }
      if (prevIndex !== currentIndex && currentIndex !== undefined) {
        _this.unhighlightComponentAt(prevIndex);
        _this.highlightComponentAt(currentIndex);
        _this.onHighlightedCallback(currentIndex);
      }
    });
    _defineProperty(_this, "attach", function (listenerNode) {
      if (listenerNode) {
        listenerNode.addEventListener('keydown', _this.onKeyDown);
      }
    });
    _defineProperty(_this, "detach", function (listenerNode) {
      if (listenerNode) {
        listenerNode.removeEventListener('keydown', _this.onKeyDown);
      }
    });
    return _this;
  }
  _inherits(KeyboardHighlightProvider, _React$Component);
  return _createClass(KeyboardHighlightProvider, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.attach(this.props.listenerNode);
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      if (prevProps.listenerNode !== this.props.listenerNode) {
        this.detach(prevProps.listenerNode);
        this.attach(this.props.listenerNode);
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.detach(this.props.listenerNode);
    }
  }, {
    key: "render",
    value: function render() {
      return /*#__PURE__*/React.createElement(KeyboardHighlightContext.Provider, {
        value: {
          registerKeyboardEnabled: this.registerKeyboardEnabled,
          unregisterKeyboardEnabled: this.unregisterKeyboardEnabled,
          getSelectedIndex: this.getSelectedIndex,
          highlightComponentAt: this.highlightComponentAt,
          unhighlightComponentAt: this.unhighlightComponentAt,
          unhighlightAll: this.unhighlightAll,
          keyboardNavComponents: this.keyboardNavComponents
        }
      }, this.props.children);
    }
  }]);
}(React.Component);
export var useKeyboardHighlightProvider = function useKeyboardHighlightProvider() {
  return useContext(KeyboardHighlightContext);
};